import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

const ACT = [
  { value: 1.2, label: 'Sedentary' },
  { value: 1.375, label: 'Light: 1-3 days/week' },
  { value: 1.55, label: 'Moderate: 3-5 days/week' },
  { value: 1.725, label: 'Active: 6-7 days/week' },
  { value: 1.9, label: 'Very active' },
];
const GOALS = [
  { value: -500, label: 'Lose weight (0.5 kg/week)' },
  { value: 0, label: 'Maintain weight' },
  { value: 500, label: 'Gain weight (0.5 kg/week)' },
];

export default function FatIntakeCalc() {
  const [tab, setTab] = useState(0);
  const [age, setAge] = useState(25);
  const [gender, setGender] = useState('male');
  const [ft, setFt] = useState(5);
  const [inches, setInches] = useState(10);
  const [lb, setLb] = useState(160);
  const [cm, setCm] = useState(178);
  const [kg, setKg] = useState(73);
  const [activity, setActivity] = useState(1.55);
  const [goal, setGoal] = useState(0);
  const [fatPct, setFatPct] = useState(30);
  const clear = () => { setAge(25); setGender('male'); setFt(5); setInches(10); setLb(160); setCm(178); setKg(73); setActivity(1.55); setGoal(0); setFatPct(30); };
  const c = useMemo(() => {
    const w = tab === 0 ? (+lb||0)*0.453592 : +kg||0;
    const h = tab === 0 ? ((+ft||0)*12+(+inches||0))*2.54 : +cm||0;
    const bmr = 10*w + 6.25*h - 5*age + (gender==='male'?5:-161);
    const tdee = bmr * activity;
    const target = tdee + goal;
    const fatCals = target * fatPct/100;
    const fatG = fatCals / 9;
    const satMax = target * 0.10 / 9;
    return { bmr, tdee, target, fatCals, fatG, satMax };
  }, [tab, age, gender, ft, inches, lb, cm, kg, activity, goal, fatPct]);

  return (
    <CalcShell look="cnet" title="Fat Intake Calculator" subtitle="Estimate daily dietary fat needs based on total energy and diet ratio.">
      <CalcNetPanel slug="fat" activeTab={tab} onTabChange={setTab} onClear={clear} right={
        <div>
          <div className="text-lg text-slate-800">Daily fat intake</div>
          <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="fat-g">{c.fatG.toFixed(0)} g / day</div>
          <ResultTable testid="fat-detail-table" rows={[
            ['BMR', `${c.bmr.toFixed(0)} kcal`],
            ['TDEE', `${c.tdee.toFixed(0)} kcal`],
            ['Daily target calories', <span className="font-bold text-[#2f9c3f]">{c.target.toFixed(0)} kcal</span>],
            [`Calories from fat (${fatPct}%)`, `${c.fatCals.toFixed(0)} kcal`],
            ['Saturated-fat cap (≤10%)', <span className="text-orange-600 font-semibold">{c.satMax.toFixed(0)} g</span>],
          ]}/>
          {/* Fat intake by target — matches Calc.net's "acceptable ranges" table */}
          <div className="mt-4">
            <h4 className="font-bold text-slate-900 mb-2 text-sm">Fat Intake Ranges (AMDR 20-35%)</h4>
            <table className="w-full text-sm border border-slate-300" data-testid="fat-range-table">
              <thead className="bg-[#1e40af] text-white"><tr><th className="px-3 py-2 text-left">Fat as % of calories</th><th className="px-3 py-2 text-right">Grams/day</th><th className="px-3 py-2 text-right">Calories/day</th></tr></thead>
              <tbody>
                {[15, 20, 25, 30, 35, 40].map((p, i) => {
                  const cal = c.target * p / 100;
                  return (
                    <tr key={p} className={i % 2 ? 'bg-slate-50' : ''}>
                      <td className="px-3 py-1.5">{p}%{p === fatPct ? ' (current)' : ''}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{(cal / 9).toFixed(0)} g</td>
                      <td className="px-3 py-1.5 text-right font-mono">{cal.toFixed(0)} kcal</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-xs text-slate-600">
            <b>Fat type recommendations:</b>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>Saturated fat: ≤ 10% of calories ({(c.target * 0.10 / 9).toFixed(0)} g/day)</li>
              <li>Polyunsaturated (omega-6 + omega-3): 5-10% ({(c.target * 0.075 / 9).toFixed(0)} g/day)</li>
              <li>Monounsaturated: fill the remainder</li>
              <li>Trans fat: as low as possible (avoid)</li>
            </ul>
          </div>
        </div>
      }>
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="fat-age"/></Row>
        <Row label="Gender"><RadioGroup name="fat-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]}/></Row>
        {tab === 0 ? <>
          <Row label="Height"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={ft} inches={inches} onFt={setFt} onIn={setInches} testidPrefix="fat-h"/></div></Row>
          <Row label="Weight"><UnitInput value={lb} onChange={setLb} unit="pounds" testid="fat-w"/></Row>
        </> : <>
          <Row label="Height"><UnitInput value={cm} onChange={setCm} unit="cm" testid="fat-hcm"/></Row>
          <Row label="Weight"><UnitInput value={kg} onChange={setKg} unit="kg" testid="fat-wkg"/></Row>
        </>}
        <Row label="Activity"><select value={activity} onChange={e => setActivity(+e.target.value)} className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" data-testid="fat-activity">{ACT.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></Row>
        <Row label="Goal"><select value={goal} onChange={e => setGoal(+e.target.value)} className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" data-testid="fat-goal">{GOALS.map(g=><option key={g.value} value={g.value}>{g.label}</option>)}</select></Row>
        <Row label="Fat %"><UnitInput value={fatPct} onChange={setFatPct} unit="%" testid="fat-pct"/></Row>
      </CalcNetPanel>
    </CalcShell>
  );
}
